import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from '../../components/Navigation';
import { useAuth } from '../../contexts/AuthContext';
import { offerService } from '../../services/offerService';
import OfferTypeSelector from './components/OfferTypeSelector';

const CURRENCIES = ['RSD', 'EUR', 'USD', 'CHF', 'GBP'];

const CATEGORIES = [
  { value: '', label: 'Odaberite kategoriju' },
  { value: 'Consulting Service', label: 'Consulting Service' },
  { value: 'Healthcare&Wellness', label: 'Healthcare & Wellness' },
  { value: 'Hospitality Services', label: 'Hospitality Services' },
  { value: 'Products&Goods', label: 'Products & Goods' },
  { value: 'Professional Services', label: 'Professional Services' },
  { value: 'Rental & Leasing', label: 'Rental & Leasing' },
  { value: 'Training & Education', label: 'Training & Education' },
  { value: 'Other', label: 'Other' },
];

export default function ObjavljPonuduForm() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [formData, setFormData] = useState({
    wishType: 'prodaja',
    voucherName: '',
    voucherSymbol: '',
    tokenQuantity: '',
    maxPrice: '',
    currency: 'RSD',
    description: '',
    specialConditions: '',
    expiryDate: '',
    exchangeWishes: '',
    category: '',
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  const validate = () => {
    const newErrors = {};
    if (!formData?.voucherName?.trim()) newErrors.voucherName = 'Naziv vaučera je obavezan';
    if (!formData?.wishType) newErrors.wishType = 'Tip oglasa je obavezan';
    if (!formData?.tokenQuantity || isNaN(formData?.tokenQuantity) || Number(formData?.tokenQuantity) <= 0)
      newErrors.tokenQuantity = 'Unesite ispravnu količinu';
    if (!formData?.maxPrice || isNaN(formData?.maxPrice) || Number(formData?.maxPrice) <= 0)
      newErrors.maxPrice = 'Unesite ispravnu cenu';
    return newErrors;
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    console.log('[ObjavljPonuduForm] handleSubmit START');

    if (!user) {
      console.warn('[ObjavljPonuduForm] No user - redirecting to login');
      navigate('/login');
      return;
    }

    console.log('[ObjavljPonuduForm] User ID:', user?.id);

    const validationErrors = validate();
    if (Object.keys(validationErrors)?.length > 0) {
      console.warn('[ObjavljPonuduForm] Validation failed:', validationErrors);
      setErrors(validationErrors);
      return;
    }

    console.log('[ObjavljPonuduForm] Validation passed. Setting loading=true...');
    setLoading(true);
    setErrors({});

    try {
      console.log('[ObjavljPonuduForm] Calling offerService.createOffer...');
      const result = await offerService?.createOffer(formData, user?.id);
      console.log('[ObjavljPonuduForm] createOffer SUCCESS:', result?.id);
      setSuccessMsg('Oglas je uspešno objavljen!');
      setTimeout(() => navigate('/oglasna-tabla-board'), 1500);
    } catch (err) {
      console.error('[ObjavljPonuduForm] createOffer FAILED:', err?.message);
      setErrors({ submit: `Greška: ${err?.message || 'Pokušajte ponovo.'}` });
    } finally {
      console.log('[ObjavljPonuduForm] handleSubmit DONE. Setting loading=false.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Objavi ponudu</h1>
          <p className="text-gray-500 mt-1">Popunite formular da biste objavili oglas na tržištu vaučera</p>
        </div>

        {successMsg && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 font-medium">
            {successMsg}
          </div>
        )}

        {errors?.submit && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {errors?.submit}
          </div>
        )}

        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-6">
          {/* Tip oglasa */}
          <OfferTypeSelector
            value={formData?.wishType}
            onChange={(v) => handleChange('wishType', v)}
            error={errors?.wishType}
          />

          {/* Kategorija */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Kategorija</label>
            <select
              value={formData?.category}
              onChange={(e) => handleChange('category', e?.target?.value)}
              className="w-full px-3 py-2.5 min-h-[44px] border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white text-base"
            >
              {CATEGORIES?.map(opt => (
                <option key={opt?.value} value={opt?.value}>{opt?.label}</option>
              ))}
            </select>
          </div>

          {/* Naziv vaučera */}
          <div>
            <div className="flex flex-wrap items-baseline gap-x-2 mb-1">
              <label className="text-sm font-medium text-gray-700">
                Naziv vaučera <span className="text-red-500">*</span>
              </label>
              <p className="text-xs text-gray-500">
                upišite TAČAN naziv vaučera (name) iz kataloga valuebook.app
              </p>
            </div>
            <input
              type="text"
              value={formData?.voucherName}
              onChange={(e) => handleChange('voucherName', e?.target?.value)}
              placeholder="npr. ValueBook Token"
              className={`w-full px-3 py-2.5 min-h-[44px] border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-base ${
                errors?.voucherName ? 'border-red-400' : 'border-gray-300'
              }`}
            />
            {errors?.voucherName && <p className="text-red-500 text-xs mt-1">{errors?.voucherName}</p>}
          </div>

          {/* Oznaka (simbol) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Oznaka (symbol) iz kataloga valuebook.app</label>
            <input
              type="text"
              value={formData?.voucherSymbol}
              onChange={(e) => handleChange('voucherSymbol', e?.target?.value?.toUpperCase())}
              placeholder="npr. VBT"
              maxLength={10}
              className="w-full px-3 py-2.5 min-h-[44px] border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 uppercase text-base"
            />
          </div>

          {/* Količina i Cena */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Količina <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="1"
                value={formData?.tokenQuantity}
                onChange={(e) => handleChange('tokenQuantity', e?.target?.value)}
                placeholder="0"
                className={`w-full px-3 py-2.5 min-h-[44px] border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-base ${
                  errors?.tokenQuantity ? 'border-red-400' : 'border-gray-300'
                }`}
              />
              {errors?.tokenQuantity && <p className="text-red-500 text-xs mt-1">{errors?.tokenQuantity}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Očekivana cena po jedinici <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2">
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData?.maxPrice}
                  onChange={(e) => handleChange('maxPrice', e?.target?.value)}
                  placeholder="0.00"
                  className={`flex-1 px-3 py-2.5 min-h-[44px] border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-base ${
                    errors?.maxPrice ? 'border-red-400' : 'border-gray-300'
                  }`}
                />
                <select
                  value={formData?.currency}
                  onChange={(e) => handleChange('currency', e?.target?.value)}
                  className="px-3 py-2.5 min-h-[44px] border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white text-base"
                >
                  {CURRENCIES?.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              {errors?.maxPrice && <p className="text-red-500 text-xs mt-1">{errors?.maxPrice}</p>}
            </div>
          </div>

          {/* Opis */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Opis</label>
            <textarea
              value={formData?.description}
              onChange={(e) => handleChange('description', e?.target?.value)}
              placeholder="Opišite vaučer, njegovu vrednost i namenu..."
              rows={4}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none text-base"
            />
          </div>

          {/* Uslovi */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Posebni uslovi</label>
            <textarea
              value={formData?.specialConditions}
              onChange={(e) => handleChange('specialConditions', e?.target?.value)}
              placeholder="Navedite posebne uslove kupovine/prodaje/razmene..."
              rows={3}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none text-base"
            />
          </div>

          {/* Željeni vaučeri za razmenu */}
          {formData?.wishType === 'razmena' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Upišite koje vrste vaučera bi želeli da dobijete u zamenu i u kojoj količini
              </label>
              <textarea
                value={formData?.exchangeWishes}
                onChange={(e) => handleChange('exchangeWishes', e?.target?.value)}
                placeholder="npr. 2x ValueBook Token (VBT), 1x neki drugi vaučer..."
                rows={4}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none text-base"
              />
            </div>
          )}

          {/* Datum isteka */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ponuda važi do</label>
            <input
              type="date"
              value={formData?.expiryDate}
              min={new Date()?.toISOString()?.split('T')?.[0]}
              onChange={(e) => handleChange('expiryDate', e?.target?.value)}
              className="w-full px-3 py-2.5 min-h-[44px] border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-base"
            />
          </div>

          {/* Katalog info */}
          <div className="p-4 bg-teal-50 border border-teal-200 rounded-lg text-sm text-teal-800">
            Detalje token-vaučera možete filtrirati i pregledati na katalogu{' '}
            <span className="font-bold">
              <a
                href="https://valuebook.app"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-teal-600"
              >
                valuebook.app
              </a>
            </span>
          </div>

          {/* Submit */}
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <button
              type="button"
              onClick={() => navigate('/oglasna-tabla-board')}
              className="flex-1 px-4 min-h-[48px] border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium text-base"
            >
              Otkaži
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 min-h-[48px] bg-teal-700 text-white rounded-lg hover:bg-teal-800 transition-colors font-medium disabled:opacity-60 disabled:cursor-not-allowed text-base"
            >
              {loading ? 'Objavljivanje...' : 'Objavi'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
